/**
 * Locale store — the single source of truth for the active UI language.
 *
 * Built on zustand (same pattern as `stores/ui-store.ts`) so any component can
 * subscribe and re-render on a language change. The choice is persisted to
 * localStorage under `psn-locale` (the raw locale string — backward compatible
 * with the previous ad-hoc Profile selector), and every change applies the two
 * document-level side effects the rest of the app relies on:
 *
 *   • `document.documentElement.lang = <locale>`  (a11y / spellcheck / SEO)
 *   • `document.documentElement.dir  = 'rtl' | 'ltr'`  (RTL mirroring for `ar`)
 *
 * `i18n.changeLanguage(locale)` is exposed as a familiar, framework-style handle
 * (mirrors react-i18next's API) that simply drives this store.
 */

import { create } from 'zustand';

import {
  DEFAULT_LOCALE,
  LOCALE_STORAGE_KEY,
  type Locale,
  dirFor,
  isLocale,
} from './config';

/** Read the persisted locale, coercing any legacy/absent value to the default. */
function readInitialLocale(): Locale {
  try {
    const raw = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (isLocale(raw)) return raw;
  } catch {
    /* private mode / disabled storage → fall through to the default */
  }
  return DEFAULT_LOCALE;
}

/** Apply `lang` + `dir` to <html>. No-op outside the browser (SSR-safe). */
export function applyLocaleToDocument(locale: Locale): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.setAttribute('lang', locale);
  root.setAttribute('dir', dirFor(locale));
}

/** Persist the raw locale string; ignore quota / private-mode failures. */
function persistLocale(locale: Locale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    /* ignore */
  }
}

interface LocaleState {
  locale: Locale;
  /** Switch language: updates state, persists, and flips <html> lang + dir. */
  setLocale: (locale: Locale) => void;
}

export const useLocaleStore = create<LocaleState>((set, get) => ({
  locale: readInitialLocale(),
  setLocale: (locale) => {
    if (!isLocale(locale) || locale === get().locale) {
      // Still re-apply document attributes for the initial/identical case so a
      // fresh reload always reflects the persisted choice.
      if (isLocale(locale)) applyLocaleToDocument(locale);
      return;
    }
    persistLocale(locale);
    applyLocaleToDocument(locale);
    set({ locale });
  },
}));

// Apply the persisted locale to <html> as soon as this module loads, so the
// very first paint already has the correct lang + direction (mirrors the way
// ui-store applies the persisted theme on load).
applyLocaleToDocument(useLocaleStore.getState().locale);

/**
 * Framework-style handle. `changeLanguage` is the canonical mutation the app
 * (and the Profile selector) calls; `language` / `dir` are convenience reads for
 * non-React callers.
 */
export const i18n = {
  get language(): Locale {
    return useLocaleStore.getState().locale;
  },
  changeLanguage(locale: Locale): void {
    useLocaleStore.getState().setLocale(locale);
  },
  dir(locale: Locale = useLocaleStore.getState().locale): 'ltr' | 'rtl' {
    return dirFor(locale);
  },
};
